import {
  Box,
  Button,
  FormControl,
  MessageBlock,
  Textarea,
} from '@deckee/deck-hand';
import classNames from 'classnames/bind';
import { Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as React from 'react';
import * as yup from 'yup';
import captureSpecificError from '../../../services/sentry';
import {
  SHARING_MESSAGE__ERROR,
  SHARING_MESSAGE__LOADING,
} from '../../../configurations/constants';
import { captureEvent } from '../../../services/event-capture';
import { submitShare } from '../../../services/share';
import { sharingMessageSuccess } from '../../helpers/share-helpers';
import HeaderBar from '../../layouts/components/HeaderBar/HeaderBar';
import ControlButton, {
  SHAPES,
  SIZES,
  TYPES,
} from '../../shared/_deprecated/components/ControlButton/ControlButton';
import AutoComplete from './AutoComplete';
import styles from './SharingDialog.scss';

const cx = classNames.bind(styles);

const ShareNowSchema = yup.object({
  sharedToMember: yup.string().required('Please select a user to share with'),
  message: yup.string().notRequired(),
});

const ShareNow = ({
  path,
  title,
  description,
  type,
  id,
  onSuccess,
  onDismiss,
  submissionSuccess,
}) => {
  const [error, setError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const captureEventData = React.useCallback(
    async (eventName) => {
      await captureEvent({
        type: eventName,
        payload: {
          itemType: type,
          itemId: id,
          url: path,
        },
      });
    },
    [id, path, type],
  );

  React.useEffect(() => {
    async function captureLoadEventData() {
      captureEventData('shareNow: load');
    }
    captureLoadEventData();
  }, [captureEventData]);

  const submitHandler = React.useCallback(
    async (values, { setSubmitting }) => {
      try {
        setIsLoading(true);
        captureEventData('shareNow: submit');

        await submitShare({
          path,
          title,
          description,
          type,
          id,
          ...values,
        });

        onSuccess();
      } catch (err) {
        captureSpecificError(
          err,
          'containers/app-main-client/src/components/features/Sharing/ShareNow.jsx',
        );
        setError(err);
      } finally {
        setSubmitting(false);
        setIsLoading(false);
      }
    },
    [description, id, path, title, type, onSuccess, captureEventData],
  );

  // // flag for all success conditions.
  const showSuccess = !error && !isLoading && submissionSuccess;

  return (
    <Box>
      <HeaderBar
        leftNode={
          <ControlButton
            key="button-back"
            action={() => onDismiss()}
            controlButtonType={TYPES.RETURN}
            shape={SHAPES.ROUND}
            size={SIZES.SMALLEST}
          />
        }
        centerNode="Share via Deckee"
      />
      {isLoading ? (
        <MessageBlock className={cx('sharing-dialog__message-block')}>
          {SHARING_MESSAGE__LOADING}
        </MessageBlock>
      ) : null}

      {error ? (
        <MessageBlock
          className={cx('sharing-dialog__message-block')}
          type="error"
        >
          {SHARING_MESSAGE__ERROR}
        </MessageBlock>
      ) : null}

      {showSuccess ? (
        <MessageBlock
          className={cx('sharing-dialog__message-block')}
          type="success"
        >
          {sharingMessageSuccess(type)}
        </MessageBlock>
      ) : null}

      {!showSuccess ? (
        <Formik
          initialValues={{ sharedToMember: '', comment: '' }}
          initialErrors={{ sharedToMember: 'required' }}
          validationSchema={ShareNowSchema}
          onSubmit={submitHandler}
        >
          {({ isSubmitting, handleChange, values, isValid }) => (
            <Form
              className={cx('sharing-dialog__share-now-form')}
              autoComplete="off"
            >
              <div className={cx('sharing-dialog__member-and-search')}>
                <div className={cx('sharing-dialog__header-details')}>
                  <Field
                    type="string"
                    id="sharedToMember"
                    name="sharedToMember"
                  >
                    {({ form: { setFieldValue } }) => (
                      <AutoComplete
                        autoFocus
                        onValueChange={(newValue) => {
                          setFieldValue('sharedToMember', newValue);
                        }}
                      />
                    )}
                  </Field>
                </div>
              </div>
              <FormControl label="Comment">
                <Textarea
                  value={values.comment}
                  onChange={handleChange}
                  rows={3}
                  name="comment"
                />
              </FormControl>

              <Button.Group my="10px" wide>
                <Button type="submit" disabled={isSubmitting || !isValid}>
                  Share now
                </Button>
              </Button.Group>
            </Form>
          )}
        </Formik>
      ) : null}
    </Box>
  );
};

ShareNow.propTypes = {
  path: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  submissionSuccess: PropTypes.bool,
  id: PropTypes.string,
};

ShareNow.defaultProps = {
  id: undefined,
  description: undefined,
  title: undefined,
  submissionSuccess: false,
};

export default ShareNow;
